import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FieldScheduleEntry,
  ScheduleEntryType,
  CreateScheduleEntryRequest,
  ENTRY_TYPE_LABELS,
  ENTRY_TYPE_COLORS
} from '../../types/schedule';
import ConfirmModal from '../common/ConfirmModal';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: var(--legacy-card-bg, #0d1b2a);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #00d4ff;
  font-family: 'Orbitron', sans-serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #8bb8d8;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #8bb8d8;
  margin-bottom: 6px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }

  option {
    background: #0d1b2a;
    color: #fff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const RefereeCheckboxList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 8px;
`;

const RefereeCheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #00d4ff;
    cursor: pointer;
  }

  span {
    color: #fff;
    font-size: 14px;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(255, 68, 68, 0.2);
  border: 1px solid #ff4444;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  color: #ff4444;
  font-size: 13px;
`;

const TimeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const TimePickerRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const TimeSelect = styled.select`
  padding: 8px 4px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  width: 50px;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }

  option {
    background: #0d1b2a;
    color: #fff;
  }
`;

const PeriodSelect = styled(TimeSelect)`
  width: 55px;
  font-weight: 600;
`;

const TypeSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TypeButton = styled.button<{ $active: boolean; $color: string }>`
  padding: 8px 16px;
  border-radius: 6px;
  border: 2px solid ${props => props.$active ? props.$color : 'rgba(255,255,255,0.2)'};
  background: ${props => props.$active ? props.$color + '20' : 'transparent'};
  color: ${props => props.$active ? props.$color : '#8bb8d8'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.$color};
    color: ${props => props.$color};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid rgba(0, 212, 255, 0.2);
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #00d4ff;
          border: none;
          color: #000;
          &:hover { background: #00a8cc; }
        `;
      case 'danger':
        return `
          background: #ff4444;
          border: none;
          color: #fff;
          &:hover { background: #cc3333; }
        `;
      default:
        return `
          background: transparent;
          border: 1px solid rgba(255,255,255,0.2);
          color: #8bb8d8;
          &:hover { border-color: #fff; color: #fff; }
        `;
    }
  }}
`;

interface RefereeOption {
  id: string;
  name: string;
}

interface ScheduleEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateScheduleEntryRequest) => Promise<void>;
  onDelete?: () => Promise<void>;
  entry?: FieldScheduleEntry | null;
  selectedDate?: Date;
  referees?: RefereeOption[];
}

const ScheduleEntryModal: React.FC<ScheduleEntryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  entry,
  selectedDate,
  referees = []
}) => {
  const [entryType, setEntryType] = useState<ScheduleEntryType>('referee_shift');
  const [selectedRefereeIds, setSelectedRefereeIds] = useState<string[]>([]);
  const [gameType, setGameType] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState('');
  // Time in 12-hour format
  const [startHour, setStartHour] = useState('9');
  const [startMinute, setStartMinute] = useState('00');
  const [startPeriod, setStartPeriod] = useState<'AM' | 'PM'>('AM');
  const [endHour, setEndHour] = useState('6');
  const [endMinute, setEndMinute] = useState('00');
  const [endPeriod, setEndPeriod] = useState<'AM' | 'PM'>('PM');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Конвертирует 24h время в 12h формат
  const to12Hour = (hour24: number): { hour: string; period: 'AM' | 'PM' } => {
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;
    return { hour: hour12.toString(), period };
  };

  // Конвертирует 12h время в 24h формат (HH:MM)
  const to24Hour = (hour12: string, minute: string, period: 'AM' | 'PM'): string => {
    let hour24 = parseInt(hour12);
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    if (period === 'AM' && hour24 === 12) hour24 = 0;
    return `${hour24.toString().padStart(2, '0')}:${minute}`;
  };

  // Извлекает время из строки даты и возвращает компоненты 12h формата
  const extractTime = (dateTimeStr: string): { hour: string; minute: string; period: 'AM' | 'PM' } => {
    const timePart = dateTimeStr.split('T')[1];
    if (!timePart) return { hour: '9', minute: '00', period: 'AM' };

    const [hours, minutes] = timePart.substring(0, 5).split(':').map(Number);
    const { hour, period } = to12Hour(hours);
    return { hour, minute: minutes.toString().padStart(2, '0'), period };
  };

  // Форматирует локальную дату в YYYY-MM-DD
  const formatLocalDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    setError(null);
    if (entry) {
      setEntryType(entry.entryType);
      // Собираем все ID рефери из обоих полей
      const refIds: string[] = [];
      if (entry.refereeId) refIds.push(entry.refereeId);
      if (entry.refereeIds) {
        entry.refereeIds.forEach(id => {
          if (id && !refIds.includes(id)) refIds.push(id);
        });
      }
      setSelectedRefereeIds(refIds);
      setGameType(entry.gameType || '');
      setTitle(entry.title || '');
      setDescription(entry.description || '');
      setNotes(entry.notes || '');
      setDate(entry.date.split('T')[0]);
      // Parse times into 12h format
      const start = extractTime(entry.startTime);
      setStartHour(start.hour);
      setStartMinute(start.minute);
      setStartPeriod(start.period);
      const end = extractTime(entry.endTime);
      setEndHour(end.hour);
      setEndMinute(end.minute);
      setEndPeriod(end.period);
    } else if (selectedDate) {
      setDate(formatLocalDate(selectedDate));
      setEntryType('referee_shift');
      setSelectedRefereeIds([]);
      setGameType('');
      setTitle('');
      setDescription('');
      setNotes('');
      // Default: 9:00 AM - 6:00 PM
      setStartHour('9');
      setStartMinute('00');
      setStartPeriod('AM');
      setEndHour('6');
      setEndMinute('00');
      setEndPeriod('PM');
    }
  }, [entry, selectedDate]);

  if (!isOpen) return null;

  const handleRefereeToggle = (refId: string) => {
    setSelectedRefereeIds(prev => {
      if (prev.includes(refId)) {
        return prev.filter(id => id !== refId);
      } else {
        return [...prev, refId];
      }
    });
    setError(null);
  };

  const handleSave = async () => {
    if (!date) return;

    setSaving(true);
    setError(null);
    try {
      // Конвертируем 12h время в 24h формат для API
      const startTime24 = to24Hour(startHour, startMinute, startPeriod);
      const endTime24 = to24Hour(endHour, endMinute, endPeriod);

      // Отправляем дату и время без конвертации в UTC чтобы избежать сдвига дня
      const startDateTime = `${date}T${startTime24}:00`;
      const endDateTime = `${date}T${endTime24}:00`;

      const data: CreateScheduleEntryRequest = {
        entryType,
        startTime: startDateTime,
        endTime: endDateTime,
        // Для обратной совместимости: первый рефери в refereeId, остальные в refereeIds
        refereeId: selectedRefereeIds.length > 0 ? selectedRefereeIds[0] : undefined,
        refereeIds: selectedRefereeIds.length > 0 ? selectedRefereeIds : undefined,
        gameType: entryType === 'referee_shift' && gameType ? gameType : undefined,
        title: entryType !== 'referee_shift' ? title : undefined,
        description: entryType !== 'referee_shift' ? description : undefined,
        notes: notes || undefined
      };

      await onSave(data);
      onClose();
    } catch (err: any) {
      console.error('Error saving schedule entry:', err);
      // Показываем ошибку от сервера (конфликт рефери)
      const message = err?.response?.data?.message || err?.message || 'Failed to save entry';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete) return;

    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (error) {
      console.error('Error deleting schedule entry:', error);
    } finally {
      setSaving(false);
    }
  };

  const isValid = () => {
    if (!date || !startHour || !endHour) return false;
    // Все типы записей требуют хотя бы одного рефери
    if (selectedRefereeIds.length === 0) return false;
    if (entryType !== 'referee_shift' && !title) return false;
    return true;
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{entry ? 'Edit Entry' : 'New Entry'}</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormGroup>
            <Label>Entry Type</Label>
            <TypeSelector>
              {(Object.keys(ENTRY_TYPE_LABELS) as ScheduleEntryType[]).map(type => (
                <TypeButton
                  key={type}
                  $active={entryType === type}
                  $color={ENTRY_TYPE_COLORS[type]}
                  onClick={() => setEntryType(type)}
                >
                  {ENTRY_TYPE_LABELS[type]}
                </TypeButton>
              ))}
            </TypeSelector>
          </FormGroup>

          <FormGroup>
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </FormGroup>

          <TimeRow>
            <FormGroup>
              <Label>Start Time</Label>
              <TimePickerRow>
                <TimeSelect value={startHour} onChange={e => setStartHour(e.target.value)}>
                  {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </TimeSelect>
                <span style={{ color: '#fff' }}>:</span>
                <TimeSelect value={startMinute} onChange={e => setStartMinute(e.target.value)}>
                  {['00', '15', '30', '45'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </TimeSelect>
                <PeriodSelect value={startPeriod} onChange={e => setStartPeriod(e.target.value as 'AM' | 'PM')}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </PeriodSelect>
              </TimePickerRow>
            </FormGroup>
            <FormGroup>
              <Label>End Time</Label>
              <TimePickerRow>
                <TimeSelect value={endHour} onChange={e => setEndHour(e.target.value)}>
                  {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </TimeSelect>
                <span style={{ color: '#fff' }}>:</span>
                <TimeSelect value={endMinute} onChange={e => setEndMinute(e.target.value)}>
                  {['00', '15', '30', '45'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </TimeSelect>
                <PeriodSelect value={endPeriod} onChange={e => setEndPeriod(e.target.value as 'AM' | 'PM')}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </PeriodSelect>
              </TimePickerRow>
            </FormGroup>
          </TimeRow>

          {/* Referee selection for all entry types */}
          <FormGroup>
            <Label>
              {entryType === 'referee_shift' ? 'Referee *' : 'Referees *'}
              {selectedRefereeIds.length > 0 && ` (${selectedRefereeIds.length} selected)`}
            </Label>
            <RefereeCheckboxList>
              {referees.map(ref => (
                <RefereeCheckboxItem key={ref.id}>
                  <input
                    type="checkbox"
                    checked={selectedRefereeIds.includes(ref.id)}
                    onChange={() => handleRefereeToggle(ref.id)}
                  />
                  <span>{ref.name}</span>
                </RefereeCheckboxItem>
              ))}
              {referees.length === 0 && (
                <div style={{ color: '#8bb8d8', padding: '8px', textAlign: 'center' }}>
                  No referees available
                </div>
              )}
            </RefereeCheckboxList>
          </FormGroup>

          {entryType === 'referee_shift' && (
            <FormGroup>
              <Label>Game Type (optional)</Label>
              <Select value={gameType} onChange={e => setGameType(e.target.value)}>
                <option value="">Any</option>
                <option value="airsoft">Airsoft</option>
                <option value="paintball">Paintball</option>
                <option value="speedball">Speedball</option>
              </Select>
            </FormGroup>
          )}

          {entryType !== 'referee_shift' && (
            <>
              <FormGroup>
                <Label>Title *</Label>
                <Input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Enter event title"
                />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Enter event description"
                />
              </FormGroup>
            </>
          )}

          <FormGroup>
            <Label>Notes</Label>
            <TextArea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional notes..."
              style={{ minHeight: '60px' }}
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          {entry && onDelete && (
            <Button $variant="danger" onClick={handleDeleteClick} disabled={saving}>
              Delete
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button $variant="primary" onClick={handleSave} disabled={saving || !isValid()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Schedule Entry"
        message="Are you sure you want to delete this schedule entry? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </Overlay>
  );
};

export default ScheduleEntryModal;
